import React from 'react';
import { Box, IconButton, Tooltip, Divider, Paper } from '@mui/material';
import {
  AttachFileOutlined,
  ChatBubbleOutlineOutlined,
  VerifiedOutlined,
  NotificationsOutlined,
} from '@mui/icons-material';
import type { FlatExecuteStep } from '@/types/run-stage.types';
import { useExecuteStepStore, type SidebarPanel } from '@/run-stage/store/executeStepStore';
import { UploadsPanel } from './sidebar/UploadsPanel';
import { CommentsPanel } from './sidebar/CommentsPanel';
import { ApprovalPanel } from './sidebar/ApprovalPanel';
import { NotificationPanel } from './sidebar/NotificationPanel';

interface ExecuteSidebarProps {
  currentStep: FlatExecuteStep | undefined;
  isReadOnly?: boolean;
}

const PANELS: Array<{ key: SidebarPanel; icon: React.ReactNode; label: string }> = [
  { key: 'uploads', icon: <AttachFileOutlined fontSize="small" />, label: 'Uploads' },
  { key: 'comments', icon: <ChatBubbleOutlineOutlined fontSize="small" />, label: 'Comments' },
  { key: 'approval', icon: <VerifiedOutlined fontSize="small" />, label: 'Approvals' },
  { key: 'notifications', icon: <NotificationsOutlined fontSize="small" />, label: 'Notifications' },
];

export const ExecuteSidebar: React.FC<ExecuteSidebarProps> = ({ currentStep, isReadOnly }) => {
  const activeSidebarPanel = useExecuteStepStore((s) => s.activeSidebarPanel);
  const toggleSidebarPanel = useExecuteStepStore((s) => s.toggleSidebarPanel);

  const step = currentStep?.step;

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Expanded panel */}
      {activeSidebarPanel && (
        <Paper
          elevation={2}
          sx={{
            width: 320,
            height: '100%',
            overflow: 'auto',
            borderLeft: '1px solid',
            borderColor: 'divider',
          }}
        >
          {activeSidebarPanel === 'uploads' && (
            <UploadsPanel
              files={step?.files ?? []}
              isReadOnly={isReadOnly}
            />
          )}
          {activeSidebarPanel === 'comments' && (
            <CommentsPanel
              comments={[]}
              isReadOnly={isReadOnly}
            />
          )}
          {activeSidebarPanel === 'approval' && (
            <ApprovalPanel
              approvers={step?.approval?.approvers ?? []}
              isReadOnly={isReadOnly}
            />
          )}
          {activeSidebarPanel === 'notifications' && (
            <NotificationPanel
              users={
                step?.notifications?.users?.map((u) => ({
                  ...u,
                  type: 'After Step',
                })) ?? []
              }
            />
          )}
        </Paper>
      )}

      {/* Icon strip */}
      <Box
        sx={{
          width: 48,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          py: 1,
          borderLeft: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        {PANELS.map((panel) => (
          <Tooltip key={panel.key} title={panel.label} placement="left">
            <IconButton
              size="small"
              onClick={() => toggleSidebarPanel(panel.key)}
              color={activeSidebarPanel === panel.key ? 'primary' : 'default'}
              sx={{
                backgroundColor: activeSidebarPanel === panel.key ? 'action.selected' : 'transparent',
                borderRadius: 1,
              }}
            >
              {panel.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};
